import { Filter, GanttInstance, FilterQuery, SortCriteria } from './types';
import {
  DEFAULT_SORT_CRITERIA,
  formatDate,
  getScrollState,
  restoreScrollState
} from './utils.ts';
import {
  clearMarks,
  buildFilterQuery,
  applyHierarchicalFilters,
  evaluateFilterQuery
} from './filters.ts';
import {
  buildSortQuery,
  createHierarchicalSortFunction,
  createConstraintsSortFunction
} from './sorting.ts';

/**
 * Main filter application function using dhtmlx gantt methods
 * with hierarchical filtering rules
 *
 * @param gantt - The Gantt instance
 * @param filters - The filters to apply
 * @param useOrLogic - Whether to use OR logic for the filters
 * @param refreshData - Whether to refresh the Gantt data after applying the filters
 */
export const applyFilters = (
  gantt: GanttInstance,
  filters: Filter[],
  useOrLogic: boolean = false,
  refreshData: boolean = true
): void => {
  if (!gantt) return;

  const query = buildFilterQuery(filters, useOrLogic);
  if (query.conditions.length === 0) {
    clearMarks(gantt, 'filtered');
  } else {
    applyHierarchicalFilters(gantt, query, 'filtered');
  }

  if (refreshData && gantt.refreshData) {
    gantt.refreshData();
  }
};

/**
 * Toggles preview mode filtering (similar to showTaskInWeeklyRange)
 * This function replicates the weekly range filtering logic from LookaheadFilterHeader
 * using multiple standard date filters
 *
 * @param gantt - The Gantt instance
 * @param dates - Object with from and to date range
 * @param turnOn - Whether to turn on or off the preview mode
 * @param refreshData - Whether to refresh gantt data after applying filters
 */
export const applyPreviewFilters = (
  gantt: GanttInstance,
  dates: { from: number | string | Date; to: number | string | Date },
  turnOn: boolean = true,
  refreshData: boolean = true
): void => {
  if (!gantt) return;

  const { from, to } = dates;
  if (!from || !to) return;

  if (turnOn) {
    const query: FilterQuery = {
      conditions: [
        {
          field: 'preview_mode',
          value: `${formatDate(from)}|${formatDate(to)}`,
          operator: false,
          dataType: 'date',
          extractFrom: null
        }
      ],
      logicalOperator: 'AND'
    };

    applyHierarchicalFilters(gantt, query, 'previewed');
  } else {
    clearMarks(gantt, 'previewed');
  }

  if (refreshData && gantt.refreshData) {
    gantt.refreshData();
  }
};

/**
 * Apply filters to constraints data
 * @param data - The data to apply filters to
 * @param filters - The filters to apply
 * @param useOrLogic - Whether to use OR logic for the filters
 */
export const applyFiltersToConstraints = (
  data: any[],
  filters: Filter[],
  useOrLogic: boolean = false
): void => {
  if (!data || !Array.isArray(data)) return;

  const query = buildFilterQuery(filters, useOrLogic);
  data.forEach((activity) => {
    if (activity.tasks && Array.isArray(activity.tasks)) {
      activity.tasks.forEach((task: any) => {
        task.hide = query.conditions.length
          ? !evaluateFilterQuery(task, query)
          : false;
      });
    }
  });
};

/**
 * Applies sorting to the gantt chart using dhtmlx gantt sort method
 * Respects hierarchical structure - only sorts tasks within the same parent level
 *
 * @param gantt - The Gantt instance
 * @param sortCriteria - Array of sort criteria to apply
 */
export const applySorting = (
  gantt: GanttInstance,
  sortCriteria: SortCriteria[] = []
): void => {
  if (!gantt) {
    return;
  }

  if (!sortCriteria?.length) {
    const defaultSortFn = createHierarchicalSortFunction(DEFAULT_SORT_CRITERIA);
    gantt.sort(defaultSortFn);
    gantt.isDataOrdered = false;

    return;
  }

  const sortQuery = buildSortQuery(sortCriteria);
  const hierarchicalSortFn = createHierarchicalSortFunction(sortQuery);

  gantt.sort(hierarchicalSortFn);
  gantt.isDataOrdered = true;
};

/**
 * Apply sorting to constraints data
 * @param data - The data to apply sorting to (activities array)
 * @param sortCriteria - Array of sort criteria to apply
 */
export const applySortingToConstraints = (
  data: any[],
  sortCriteria: SortCriteria[] = []
): void => {
  if (!data || !Array.isArray(data)) return;

  // If no sort criteria provided, apply default sorting by ID
  if (!sortCriteria?.length) {
    data.forEach((activity) => {
      if (activity.tasks && Array.isArray(activity.tasks)) {
        activity.tasks.sort((a: any, b: any) => {
          const idA = Number(a.id) || 0;
          const idB = Number(b.id) || 0;
          return idA - idB;
        });
      }
    });
    return;
  }

  const sortQuery = buildSortQuery(sortCriteria);
  const sortFn = createConstraintsSortFunction(sortQuery);

  data.forEach((activity) => {
    if (activity.tasks && Array.isArray(activity.tasks)) {
      activity.tasks.sort(sortFn);
    }
  });
};

/**
 * Refresh lookahead filters
 * @param gantt - The Gantt instance
 */
export const refreshLookaheadFilters = (gantt: GanttInstance): void => {
  if (!gantt.isDataFiltered || !window.defaultView?.current) {
    return;
  }

  const isBoolean = (value: unknown) => typeof value === 'boolean';
  const { filter_query: query } = window.defaultView.current;

  if (!Array.isArray(query?.value) || !isBoolean(query?.conditionFilter)) {
    return;
  }

  const scrollState = getScrollState(gantt);
  applyFilters(gantt, query?.value, query?.conditionFilter);
  restoreScrollState(gantt, scrollState);
};
